import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, MapPin, Building2, Radio, Users, KeyRound, ShieldAlert, ChevronRight } from 'lucide-react';
import { useSecurityContext, type Sede } from '@/contexts/MaintenanceContext';
import { SedeFormDialog } from './SedeFormDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export const SedesClienteTab: React.FC<{ clienteId: string }> = ({ clienteId }) => {
  const { sedes, sistemas, eliminarSede, clientes, setClienteActual, setSedeActual } = useSecurityContext();
  const navigate = useNavigate();

  const irASistemas = (sede: Sede) => {
    const cliente = clientes.find(c => c.id === clienteId);
    if (cliente) setClienteActual(cliente);
    setSedeActual(sede);
    navigate('/sistemas-cliente');
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Sede | null>(null);

  const sedesCliente = useMemo(
    () => sedes.filter(s => s.cliente_id === clienteId).sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [sedes, clienteId]
  );

  const sistemasPorSede = useMemo(() => {
    const map: Record<string, number> = {};
    for (const s of sistemas) {
      if (s.cliente_id === clienteId && s.sede_id) {
        map[s.sede_id] = (map[s.sede_id] || 0) + 1;
      }
    }
    return map;
  }, [sistemas, clienteId]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Sedes / Emplazamientos
        </CardTitle>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-1" /> Nueva sede
        </Button>
      </CardHeader>
      <CardContent>
        {sedesCliente.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            Este cliente aún no tiene sedes. Crea una para asignarle sistemas.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {sedesCliente.map(sede => {
              const count = sistemasPorSede[sede.id] || 0;
              const contactosCount = Array.isArray(sede.contactos_cra) ? sede.contactos_cra.length : 0;
              return (
                <div
                  key={sede.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => irASistemas(sede)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); irASistemas(sede); } }}
                  className="border rounded-lg p-3 space-y-2 bg-card hover:border-primary/40 hover:bg-accent/30 transition-colors cursor-pointer group"
                  title="Ver sistemas de esta sede"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium truncate group-hover:text-primary transition-colors">{sede.nombre}</h4>
                        {!sede.activa && <Badge variant="secondary">Inactiva</Badge>}
                        <Badge variant="outline">{count} sistema{count !== 1 ? 's' : ''}</Badge>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary ml-auto" />
                      </div>
                      {sede.direccion && (
                        <p className="text-xs text-muted-foreground flex items-start gap-1 mt-1">
                          <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                          <span>
                            {sede.direccion}
                            {sede.codigo_postal || sede.poblacion ? `, ${[sede.codigo_postal, sede.poblacion].filter(Boolean).join(' ')}` : ''}
                            {sede.provincia ? ` (${sede.provincia})` : ''}
                          </span>
                        </p>
                      )}
                      {sede.contacto_nombre && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Contacto: {sede.contacto_nombre}{sede.contacto_telefono ? ` · ${sede.contacto_telefono}` : ''}
                        </p>
                      )}

                      {(sede.numero_abonado || sede.numero_abonado_incendio || sede.palabra_clave_cra || sede.palabra_coaccion || contactosCount > 0) && (
                        <div className="mt-2 pt-2 border-t border-dashed space-y-1">
                          <div className="flex flex-wrap gap-1.5">
                            {sede.numero_abonado && (
                              <Badge variant="secondary" className="text-[10px] gap-1">
                                <Radio className="h-3 w-3" /> Abonado intrusión {sede.numero_abonado}
                              </Badge>
                            )}
                            {sede.numero_abonado_incendio && (
                              <Badge variant="secondary" className="text-[10px] gap-1">
                                <Radio className="h-3 w-3" /> Abonado incendio {sede.numero_abonado_incendio}
                              </Badge>
                            )}
                            {contactosCount > 0 && (
                              <Badge variant="secondary" className="text-[10px] gap-1">
                                <Users className="h-3 w-3" /> {contactosCount} contacto{contactosCount !== 1 ? 's' : ''} CRA
                              </Badge>
                            )}
                          </div>
                          {(sede.palabra_clave_cra || sede.palabra_coaccion) && (
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                              {sede.palabra_clave_cra && (
                                <span className="inline-flex items-center gap-1">
                                  <KeyRound className="h-3 w-3 text-primary" />
                                  Clave: <span className="font-mono font-medium text-foreground">{sede.palabra_clave_cra}</span>
                                </span>
                              )}
                              {sede.palabra_coaccion && (
                                <span className="inline-flex items-center gap-1">
                                  <ShieldAlert className="h-3 w-3 text-destructive" />
                                  Coacción: <span className="font-mono font-medium text-foreground">{sede.palabra_coaccion}</span>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" onClick={() => { setEditing(sede); setDialogOpen(true); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon"><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar sede?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {count > 0
                                ? `Esta sede tiene ${count} sistema(s) asignado(s). Se marcará como inactiva en lugar de eliminarse.`
                                : 'Esta acción no se puede deshacer.'}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => eliminarSede(sede.id)}>Confirmar</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <SedeFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          clienteId={clienteId}
          sede={editing}
        />
      </CardContent>
    </Card>
  );
};
